import { Form, Formik } from 'formik'

import { NotificationType, notify } from 'src/providers/NotificationProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Heading3, Paragraph } from 'src/components/atoms/Typography'
import { FormField } from 'src/components/molecules/FormField'
import { HStack } from 'src/components/templates/HStack'

import {
  namedOperations,
  useUpdateAdminProjectMutation
} from 'src/config/generated/graphql'
import { FormFieldTypes, Option } from 'src/types/form'
import { ProjectFeaturedOn } from 'src/types/project'

import { UpdateProjectFeaturedFormValues } from './types'
import validationSchema from './validationSchema'

export interface FeatureProjectModalProps {
  projectId?: string
  featuredOn?: ProjectFeaturedOn
  closeModal: () => void
}

export const FeatureProjectModal = ({
  projectId,
  featuredOn,
  closeModal
}: FeatureProjectModalProps) => {
  const [updateProject] = useUpdateAdminProjectMutation({
    refetchQueries: [namedOperations.Query.getAdminProject],
    onCompleted: () =>
      notify({
        message: 'Successfully updated project feature',
        type: NotificationType.success
      })
  })

  const handleUpdateProjectVisibility = async (
    values: UpdateProjectFeaturedFormValues
  ) => {
    if (projectId) {
      await updateProject({
        variables: {
          projectId,
          input: {
            Id: projectId,
            FeaturedOn:
              values.FeaturedOn === ProjectFeaturedOn.None
                ? null
                : values.FeaturedOn
          }
        },
        refetchQueries: [
          namedOperations.Query.getAdminProject,
          namedOperations.Query.getProjectNavState
        ],
        onCompleted: closeModal
      })
    }
  }

  const featureOptions: Array<Option> = [
    {
      label: 'Business',
      value: ProjectFeaturedOn.Business
    },
    {
      label: 'Home',
      value: ProjectFeaturedOn.Home
    },
    {
      label: 'All (projects excluded)',
      value: ProjectFeaturedOn.All
    },
    {
      label: 'Projects',
      value: ProjectFeaturedOn.Projects
    },
    {
      label: 'None',
      value: ProjectFeaturedOn.None
    }
  ]

  return (
    <div className='w-full sm:w-[520px]'>
      <Formik
        enableReinitialize
        initialValues={{
          FeaturedOn: featuredOn === null ? ProjectFeaturedOn.None : featuredOn
        }}
        onSubmit={handleUpdateProjectVisibility}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, dirty }) => {
          return (
            <Form>
              <Heading3 className='mb-5'>Feature project</Heading3>
              <Paragraph className='mb-5'>
                Select where you would like this project to be featured on the
                website.
              </Paragraph>
              <FormField
                id='FeaturedOn'
                label=''
                checkboxLabel='Feature project on home page?'
                className='mb-0!'
                type={FormFieldTypes.radioGroup}
                options={featureOptions}
              />

              <HStack className='mt-10 flex justify-between'>
                <ButtonRebrand
                  variant='outline'
                  className='mr-2'
                  onClick={closeModal}
                >
                  Cancel
                </ButtonRebrand>
                <ButtonRebrand
                  variant='lime'
                  disabled={isSubmitting || !dirty}
                  className='mr-2'
                  type='submit'
                >
                  Save changes
                </ButtonRebrand>
              </HStack>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
